import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import styled, { css } from 'styled-components';
const Text = styled.div `
  ${transition('transform')};
`;
const ButtonInner = styled.div `
  display: inline-flex;
  align-items: center;
`;
const Border = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  svg {
    height: var(--buttonHeight);
    color: var(--brandColour);
  }
`;
export const SolidStyles = css `
  ${transition('color, background-color, border-color')};

  padding: 14px 30px;
  min-height: var(--buttonHeight);
  border-radius: 10000000px;
  background-color: var(--brandColour);

  &[data-background-placement='dark'] {
    background-color: var(--brandAltColour);

    &:hover,
    &:active {
      background-color: var(--hoverAltColour);
      color: var(--textColour);

      ${ButtonInner} {
        color: var(--textColour);
      }
    }

    ${ButtonInner} {
      color: var(--textAltColour);
    }
  }

  &[data-shadow='true'] {
    /* Generated using https://www.joshwcomeau.com/shadow-palette/ */
    --shadow-color: 0deg 0% 45%;

    box-shadow: 0px 0.1px 0.2px hsl(var(--shadow-color) / 0),
      0px 0.5px 0.8px hsl(var(--shadow-color) / 0.04),
      -0.1px 0.8px 1.2px hsl(var(--shadow-color) / 0.08),
      -0.1px 1.2px 1.8px hsl(var(--shadow-color) / 0.12),
      -0.1px 1.6px 2.4px hsl(var(--shadow-color) / 0.17),
      -0.1px 2px 3px hsl(var(--shadow-color) / 0.21),
      -0.2px 2.6px 3.9px hsl(var(--shadow-color) / 0.25),
      -0.2px 3.4px 5.1px hsl(var(--shadow-color) / 0.29),
      -0.3px 4.3px 6.5px hsl(var(--shadow-color) / 0.33),
      -0.4px 5.6px 8.4px hsl(var(--shadow-color) / 0.37);
  }

  ${ButtonInner} {
    border: none;
    color: var(--textColour);
  }

  &:hover,
  &:active {
    background-color: var(--hoverColour);
  }
`;
const OutlineStyles = css `
  ${ButtonInner} {
    position: relative;
    background-color: transparent;
    color: var(--textColour);
    border: none;

    &:after {
      ${transition('background-color')};

      content: '';
      position: absolute;
      bottom: var(--borderPosition);
      left: 0;

      height: var(--borderThickness);
      width: 100%;
      background-color: var(--brandColour);
    }
  }

  &[data-background-placement='dark'] {
    ${ButtonInner} {
      color: var(--textAltColour);
    }
  }

  &:hover {
    ${ButtonInner} {
      &:after {
        background-color: var(--hoverColour);
      }
    }

    ${Text} {
      transform: translateY(-2px);
    }
  }

  &:active {
    ${ButtonInner} {
      border-color: var(--brandColour);
      background-color: var(--activeOutlineBgColour);
      color: var(--textColour);
    }
  }
`;
const IconLeft = css `
  padding-right: 10px;
`;
const IconRight = css `
  padding-left: 10px;
`;
const Icon = styled.div `
  ${transition('transform')};

  display: inline-block;
  vertical-align: middle;
  padding-top: 2px;
  line-height: 0;
  flex: 0 0 34px;

  ${({ position }) => {
    switch (position) {
        case 'left':
            return IconLeft;
        case 'right':
            return IconRight;
        default:
            break;
    }
}}

  svg {
    max-height: 24px;
    max-width: 24px;
    width: 100%;
  }
`;
const Button = styled.button `
  --brandColour: ${brand.black};
  --textColour: ${brand.white};
  --hoverColour: ${brand.green.base};
  --brandAltColour: ${brand.white};
  --textAltColour: ${brand.black};
  --hoverAltColour: ${brand.green.base};
  --buttonHeight: 60px;
  --borderThickness: 4px;
  --borderPosition: -6px;

  display: inline-flex;
  align-items: center;
  border: none;
  padding: 0;
  background: transparent;
  user-select: none;
  text-decoration: none;

  ${Text} {
    ${fonts.montserrat.bold};
    font-size: ${fluid(16, 21)};
    line-height: ${fluid(21, 26)};
    display: inline-block;
    vertical-align: middle;
  }

  &[data-branding='blue'] {
    --brandColour: ${brand.blue.base};
    --textColour: ${brand.black};
    --hoverColour: ${brand.blue.light};
    --textAltColour: ${brand.white};
  }

  &[data-branding='green'] {
    --brandColour: ${brand.green.base};
    --textColour: ${brand.black};
    --hoverColour: ${brand.green.light};
    --textAltColour: ${brand.white};
  }

  &[data-branding='orange'] {
    --brandColour: ${brand.orange.base};
    --textColour: ${brand.black};
    --hoverColour: ${brand.orange.light};
    --textAltColour: ${brand.white};
  }

  &[data-branding='yellow'] {
    --brandColour: ${brand.yellow.base};
    --textColour: ${brand.black};
    --hoverColour: ${brand.yellow.light};
    --textAltColour: ${brand.white};
  }

  &[data-branding='black'] {
    --brandColour: ${brand.black};
    --textColour: ${brand.black};
    --hoverColour: ${brand.grey.grey80};
    --textAltColour: ${brand.white};
  }

  &[data-branding='white'] {
    --brandColour: ${brand.white};
    --textColour: ${brand.white};
    --hoverColour: ${brand.grey.grey80};
    --textAltColour: ${brand.white};
  }

  &[data-button-size='small'] {
    --borderThickness: 2px;
    --borderPosition: -2px;
    --buttonHeight: 50px;
    ${Text} {
      font-size: 14px;
    }
  }

  &[data-button-size='medium'] {
    --borderThickness: 2px;
    --borderPosition: -2px;
    --buttonHeight: 50px;
    ${Text} {
      font-size: 16px;
    }
  }

  &[data-button-size='large'] {
    --borderThickness: 4px;
    --borderPosition: -6px;
    ${Text} {
      font-size: ${fluid(18, 20)};
    }
  }

  &[data-button-size='xl'] {
    --borderThickness: 4px;
    --borderPosition: -6px;
    ${Text} {
      font-size: ${fluid(18, 24)};
    }
  }

  ${({ styleType }) => {
    switch (styleType) {
        case 'pill':
        case 'square':
            return SolidStyles;
        case 'underline':
            return OutlineStyles;
        default:
            break;
    }
}}

  &[data-style-type='square'] {
    border-radius: 8px;
  }

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &[data-get-in-touch] {
    cursor: pointer;
  }
`;
export const ButtonStyles = {
    Border,
    Button,
    ButtonInner,
    Icon,
    OutlineStyles,
    Text,
};
